import React, { useRef, useCallback, useEffect, useState } from 'react';
import ArrowImg from '../../../../Components/ArrowSvg/ArrowImg';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import styles from './DateHeader.module.css';

const DateHeader = ({ startDate, onDateChange }) => {
    const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
    const debounceTimerRef = useRef(null);
    // Local state for immediate UI updates (optimistic UI)
    const [displayDate, setDisplayDate] = useState(startDate);
    
    // Get outlet name from selected outlet
    const getOutletName = () => {
        if (!selectedOutlet || !userAssignedOutlets) return 'No Location Selected';
        const outlet = userAssignedOutlets.find(o => o.id === selectedOutlet);
        return outlet?.name || 'Unknown Location';
    };
    const formatDateRange = (start) => {
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        
        const startDay = start.getDate();
        const endDay = end.getDate();
        const startMonth = monthNames[start.getMonth()];
        const endMonth = monthNames[end.getMonth()];
        const year = start.getFullYear();
        
        if (start.getMonth() === end.getMonth()) {
            return `${startMonth} ${startDay} - ${endDay}, ${year}`;
        } else {
            return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
        }
    };

    // Get current week's Monday
    const getCurrentWeekMonday = () => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const monday = new Date(today);
        monday.setDate(today.getDate() - daysFromMonday);
        monday.setHours(0, 0, 0, 0);
        return monday;
    };

    // Sync displayDate with startDate prop when it changes from parent
    useEffect(() => {
        setDisplayDate(startDate);
    }, [startDate]);

    // Debounced API call function - only triggers the actual API
    const debouncedAPICall = useCallback((newDate) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            onDateChange(newDate);
        }, 300); // 300ms debounce delay for API call
    }, [onDateChange]);

    const handlePrevWeek = () => {
        const newDate = new Date(displayDate);
        newDate.setDate(newDate.getDate() - 7);
        // Update UI immediately (optimistic update)
        setDisplayDate(newDate);
        // Debounce the API call
        debouncedAPICall(newDate);
    };

    const handleNextWeek = () => {
        const newDate = new Date(displayDate);
        newDate.setDate(newDate.getDate() + 7);
        // Update UI immediately (optimistic update)
        setDisplayDate(newDate);
        // Debounce the API call
        debouncedAPICall(newDate);
    };

    const handleCurrentWeek = () => {
        const currentMonday = getCurrentWeekMonday();
        // Update UI immediately
        setDisplayDate(currentMonday);
        // For current week, also update immediately (no debounce)
        onDateChange(currentMonday);
    };

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return (
        <div className={styles.dateHeader}>
            <div className={styles.leftSection}>
                <span className={styles.locationLabel}>Location :</span>
                <span className={styles.locationName}>{getOutletName()}</span>
            </div>
            
            <div className={styles.rightSection}>
            <button 
                    className={styles.currentWeekButton} 
                    onClick={handleCurrentWeek}
                    aria-label="Current week"
                >
                    Current Week
                </button>
                <div className={styles.separator}></div>
                <span className={styles.dateRange}>{formatDateRange(displayDate)}</span>
            
                <div className={styles.separator}></div>
                <div className={styles.arrowButtons}>
                    <button 
                        className={styles.arrowButton} 
                        onClick={handlePrevWeek}
                        aria-label="Previous week"
                    >
                        <ArrowImg SvgClass={styles.prevArrow} />
                    </button>
                    <button 
                        className={styles.arrowButton} 
                        onClick={handleNextWeek}
                        aria-label="Next week"
                    >
                        <ArrowImg SvgClass={styles.nextArrow} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DateHeader;

